<script lang="ts">
  import { getCurrentRoute, navigate } from '$lib/router.svelte';
  import { isLoggedIn, getUser } from '$lib/auth.svelte';
  import { captureReturnTo } from '$lib/utils/returnTo';
  import { api } from '$shared/api';
  import { startPolling, stopPolling } from '$lib/stores/notifications.svelte';
  import { connect as socketConnect, disconnect as socketDisconnect } from '$lib/stores/socket.svelte';
  import { addToast } from '$shared/toasts.svelte';

  // Layouts
  import AppLayout from '$lib/layouts/AppLayout.svelte';
  import PublicLayout from '$lib/layouts/PublicLayout.svelte';
  import AuthLayout from '$lib/layouts/AuthLayout.svelte';

  // Auth pages
  import Login from '$lib/pages/auth/Login.svelte';
  import Register from '$lib/pages/auth/Register.svelte';

  // Duel pages (view-only: browse, detail, results — submit/create/score/
  // leaderboard retired with the Curation Duels cutover)
  import DuelBrowse from '$lib/pages/duels/DuelBrowse.svelte';
  import DuelDetail from '$lib/pages/duels/DuelDetail.svelte';
  import DuelResult from '$lib/pages/duels/DuelResult.svelte';
  import TrackAiScore from '$lib/pages/duels/TrackAiScore.svelte';

  // Account pages
  import AccountHub from '$lib/pages/account/AccountHub.svelte';
  import AccountProfile from '$lib/pages/account/AccountProfile.svelte';
  import AccountSecurity from '$lib/pages/account/AccountSecurity.svelte';
  import AccountNotifications from '$lib/pages/account/AccountNotifications.svelte';
  import AccountNotificationsList from '$lib/pages/account/AccountNotificationsList.svelte';
  import AccountStats from '$lib/pages/account/AccountStats.svelte';
  import AccountInvite from '$lib/pages/account/AccountInvite.svelte';
  import AccountVotes from '$lib/pages/account/AccountVotes.svelte';
  import AccountSaved from '$lib/pages/account/AccountSaved.svelte';
  import AccountListened from '$lib/pages/account/AccountListened.svelte';
  import AccountSuggestions from '$lib/pages/account/AccountSuggestions.svelte';
  import AccountActivity from '$lib/pages/account/AccountActivity.svelte';
  import AccountGenres from '$lib/pages/account/AccountGenres.svelte';
  import AccountPlans from '$lib/pages/account/AccountPlans.svelte';
  import AccountBlockedUsers from '$lib/pages/account/AccountBlockedUsers.svelte';

  // Creator catalog pages (Curation Duels)
  import MyCatalog from '$lib/pages/catalog/MyCatalog.svelte';
  import CatalogAdd from '$lib/pages/catalog/CatalogAdd.svelte';

  // Public pages
  import Landing from '$lib/pages/public/Landing.svelte';
  import UserProfile from '$lib/pages/public/UserProfile.svelte';
  import TrackView from '$lib/pages/public/TrackView.svelte';
  import Search from '$lib/pages/public/Search.svelte';
  // GlobalLeaderboard removed — /leaderboard routes to Leaderboard (player-only)
  import ArtistProfile from '$lib/pages/public/ArtistProfile.svelte';
  // Artist & Track leaderboards + Top Picks removed (player-only leaderboard); artist profiles remain
  import Points from '$lib/pages/public/Points.svelte';
  import ChallengeLanding from '$lib/pages/public/ChallengeLanding.svelte';
  import SeriesLanding from '$lib/pages/public/SeriesLanding.svelte';
  import JoinTeamLanding from '$lib/pages/public/JoinTeamLanding.svelte';
  import Leaderboard from '$lib/pages/public/Leaderboard.svelte';
  import SeasonWrapUp from '$lib/pages/public/SeasonWrapUp.svelte';

  // Auth/misc
  import Onboarding from '$lib/pages/auth/Onboarding.svelte';
  import ForgotPassword from '$lib/pages/auth/ForgotPassword.svelte';
  import ResetPassword from '$lib/pages/auth/ResetPassword.svelte';
  import VerifyEmail from '$lib/pages/auth/VerifyEmail.svelte';
  import Unsubscribe from '$lib/pages/auth/Unsubscribe.svelte';
  import ReportContent from '$lib/pages/public/ReportContent.svelte';
  // Overview — lazy loaded (74 KB raw, rarely visited)
  import SubmitGuide from '$lib/pages/public/SubmitGuide.svelte';
  import TermsOfService from '$lib/pages/public/TermsOfService.svelte';
  import PrivacyPolicy from '$lib/pages/public/PrivacyPolicy.svelte';
  import Help from '$lib/pages/public/Help.svelte';
  import Feed from '$lib/pages/Feed.svelte';
  import Friends from '$lib/pages/Friends.svelte';
  import NotFound from '$lib/pages/NotFound.svelte';
  import ErrorBoundary from '$lib/components/shared/ErrorBoundary.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import ToastContainer from '$lib/components/shared/ToastContainer.svelte';
  import FeedbackWidget from '$shared/FeedbackWidget.svelte';

  // Lazy-loaded components — split into separate chunks, loaded on first visit

  const lazyComponents: Record<string, () => Promise<{ default: any }>> = {
    DmcaSubmit: () => import('$lib/pages/public/DmcaSubmit.svelte'),
    DmcaCounterNotice: () => import('$lib/pages/public/DmcaCounterNotice.svelte'),
    Overview: () => import('$lib/pages/public/Overview.svelte'),
  };

  // Cache for resolved lazy components — loaded once, reused on revisit

  const lazyCache = new Map<string, any>();

  let LazyPage = $state<any>(null);
  let lazyLoading = $state(false);
  let lazyFailed = $state(false);

  // Static component map — eagerly loaded, used by all players

  const componentMap: Record<string, any> = {
    Login,
    Register,
    Onboarding,
    Landing,
    DuelBrowse,
    DuelDetail,
    DuelResult,
    TrackAiScore,
    AccountHub,
    AccountProfile,
    AccountSecurity,
    AccountNotifications,
    AccountNotificationsList,
    AccountStats,
    AccountInvite,
    AccountVotes,
    AccountSaved,
    AccountListened,
    AccountSuggestions,
    AccountActivity,
    AccountGenres,
    AccountPlans,
    AccountBlockedUsers,
    MyCatalog,
    CatalogAdd,
    UserProfile,
    TrackView,
    Search,
    ArtistProfile,
    Points,
    ChallengeLanding,
    SeriesLanding,
    JoinTeamLanding,
    Leaderboard,
    SeasonWrapUp,
    ForgotPassword,
    ResetPassword,
    VerifyEmail,
    Unsubscribe,
    ReportContent,
    SubmitGuide,
    TermsOfService,
    PrivacyPolicy,
    Help,
    Feed,
    Friends,
    NotFound,
  };

  // Page title map — component name → browser tab title
  const pageTitles: Record<string, string> = {
    Login: 'Log In',
    Register: 'Sign Up',
    Onboarding: "What's Your Sound?",
    ForgotPassword: 'Forgot Password',
    ResetPassword: 'Reset Password',
    VerifyEmail: 'Verify Email',
    Unsubscribe: 'Unsubscribe',
    // Note: the title effect below renders untitled routes as bare 'Beat Duel'
    // (no ' | Beat Duel' suffix) — a literal 'Beat Duel' entry here would render
    // as the redundant 'Beat Duel | Beat Duel', so the homepage gets a tagline instead.
    Landing: 'Songs Fight. You Decide.',
    DuelBrowse: 'Duels',
    DuelDetail: 'Duel',
    DuelResult: 'Duel Result',
    TrackAiScore: 'AI Score',
    AccountHub: 'Account',
    AccountProfile: 'Edit Profile',
    AccountSecurity: 'Security',
    AccountNotifications: 'Notification Settings',
    AccountNotificationsList: 'Notifications',
    AccountStats: 'Stats',
    AccountInvite: 'Invite',
    AccountVotes: 'My Votes',
    AccountSaved: 'Saved Tracks',
    AccountListened: 'Listened',
    AccountSuggestions: 'Suggestions',
    AccountActivity: 'Activity',
    AccountGenres: 'Genres',
    AccountPlans: 'Plans',
    AccountBlockedUsers: 'Blocked Users',
    MyCatalog: 'My Catalog',
    CatalogAdd: 'Add a Song',
    UserProfile: 'Player Profile',
    TrackView: 'Track',
    // TrackBrowse removed
    Search: 'Search',
    // GlobalLeaderboard removed — /leaderboard now routes to Leaderboard (player-only)
    ArtistProfile: 'Artist',
    // Artist & Track leaderboards + Top Picks removed (player-only leaderboard)
    ChallengeLanding: 'Challenge',
    SeriesLanding: 'Series',
    JoinTeamLanding: 'Join a Team',
    Leaderboard: 'Leaderboard',
    SeasonWrapUp: 'Season Wrap-Up',
    Points: 'Points',
    ReportContent: 'Report',
    Feed: 'Feed',
    Friends: 'Friends',
    DmcaSubmit: 'DMCA Takedown',
    DmcaCounterNotice: 'DMCA Counter-Notice',
    Overview: 'Product Overview',
    SubmitGuide: 'Creator Guide',
    TermsOfService: 'Terms of Service',
    PrivacyPolicy: 'Privacy Policy',
    Help: 'Help & FAQ',
    NotFound: 'Not Found',
  };

  // Auth pages use AuthLayout, public pages use PublicLayout, rest use AppLayout
  const authPages = new Set(['Login', 'Register', 'ForgotPassword', 'ResetPassword', 'VerifyEmail', 'Unsubscribe']);
  const publicPages = new Set([
    'Landing',
    'ChallengeLanding',
    'SeriesLanding',
    'JoinTeamLanding',
    'SubmitGuide',
    'TermsOfService',
    'PrivacyPolicy',
    'Help',
    'DmcaSubmit',
    'DmcaCounterNotice',
    'NotFound',
  ]);

  const route = $derived(getCurrentRoute());
  const isLazy = $derived(route.component in lazyComponents);
  const PageComponent = $derived(isLazy ? null : componentMap[route.component] || NotFound);

  // Lazy component loader — resolves dynamic imports, caches for revisits
  $effect(() => {
    const comp = route.component;
    lazyFailed = false;
    if (comp in lazyComponents) {
      if (lazyCache.has(comp)) {
        LazyPage = lazyCache.get(comp);
        lazyLoading = false;
      } else {
        lazyLoading = true;
        LazyPage = null;
        lazyComponents[comp]()
          .then((m) => {
            lazyCache.set(comp, m.default);
            LazyPage = m.default;
            lazyLoading = false;
            sessionStorage.removeItem(`chunk-reload:${comp}`);
          })
          .catch((e) => {
            console.error(`Failed to lazy-load ${comp}:`, e);
            // A deploy replaced the hashed chunks this tab's bundle references.
            // One reload picks up the fresh no-cache index.html; the flag
            // stops a reload loop if the failure is anything else.
            const key = `chunk-reload:${comp}`;
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, '1');
              window.location.reload();
            } else {
              lazyLoading = false;
              lazyFailed = true;
            }
          });
      }
    }
  });

  const layout = $derived(
    authPages.has(route.component) ? 'auth' : !isLoggedIn() && publicPages.has(route.component) ? 'public' : 'app',
  );

  // Auth guard — redirect to login if route requires auth and user isn't logged in
  $effect(() => {
    if (route.requiresAuth && !isLoggedIn()) {
      // Stash the attempted destination so a successful login/signup/OAuth can
      // return the user here instead of the default landing page.
      captureReturnTo(route.query ? `${route.path}?${route.query}` : route.path);
      navigate('/login');
    } else if (isLoggedIn() && getUser()?.onboarding_complete === false && route.component !== 'Onboarding') {
      // Takes priority over the Landing redirect below — an onboarding-incomplete
      // user goes straight to /onboarding rather than bouncing through /duels first.
      navigate('/onboarding');
    } else if (isLoggedIn() && route.path === '/') {
      // `/` is the logged-out marketing landing; a logged-in (and fully
      // onboarded) user should never see it — send them to the duel browse.
      navigate('/duels');
    }
  });

  // Update document.title and reset focus on route change
  $effect(() => {
    const title = pageTitles[route.component];
    document.title = title ? `${title} | Beat Duel` : 'Beat Duel';
    // Move focus to main content area for screen reader users
    requestAnimationFrame(() => {
      const main = document.querySelector('main');
      if (main) {
        main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      }
    });
  });

  // Start/stop notification polling and socket connection based on auth state
  let streakToastShown = false;
  $effect(() => {
    if (isLoggedIn()) {
      startPolling();
      socketConnect();
      // One-shot streak toast per session
      if (!streakToastShown) {
        streakToastShown = true;
        setTimeout(() => {
          api
            .get<{ streak: number }>('/users/current/vote-streak')
            .then((d) => {
              if (d.streak >= 2) {
                addToast(`\u{1F525} ${d.streak}-day voting streak \u2014 keep it going!`, 'points');
              }
            })
            .catch(() => {});
        }, 800);
      }
    } else {
      stopPolling();
      socketDisconnect();
    }
  });
</script>

<ErrorBoundary>
  {#if layout === 'auth'}
    <AuthLayout>
      {#key route.path}
        <PageComponent {...route.params} />
      {/key}
    </AuthLayout>
  {:else if layout === 'public'}
    <PublicLayout>
      {#key route.path}
        {#if isLazy}
          {#if lazyLoading}
            <LoadingSkeleton />
          {:else if lazyFailed}
            <div class="lazy-failed">
              <p>This page failed to load.</p>
              <button type="button" onclick={() => window.location.reload()}>Reload</button>
            </div>
          {:else if LazyPage}
            <LazyPage {...route.params} />
          {/if}
        {:else}
          <PageComponent {...route.params} />
        {/if}
      {/key}
    </PublicLayout>
  {:else}
    <AppLayout>
      {#key route.path}
        {#if isLazy}
          {#if lazyLoading}
            <LoadingSkeleton />
          {:else if lazyFailed}
            <div class="lazy-failed">
              <p>This page failed to load.</p>
              <button type="button" onclick={() => window.location.reload()}>Reload</button>
            </div>
          {:else if LazyPage}
            <LazyPage {...route.params} />
          {/if}
        {:else}
          <PageComponent {...route.params} />
        {/if}
      {/key}
    </AppLayout>
  {/if}
</ErrorBoundary>

<FeedbackWidget showPill={false} />
<ToastContainer />

<style>
  .lazy-failed {
    padding: 3rem 2rem;
    text-align: center;
    color: var(--text-muted);
  }
  .lazy-failed p {
    margin-bottom: 1rem;
  }
</style>
